import {
  FitnessCenter,
  Groups,
  GroupsOutlined,
  Person,
  PersonOutlineOutlined,
  SportsHandball,
  SportsHandballOutlined,
} from '@mui/icons-material';
import React from 'react';
import { SvgIcon } from '@mui/material';

export const PROVIDER_USER_TYPES = {
  FITNESS_VENUE: 'fitness-venue',
  FREELANCE_PERSONAL_TRAINER: 'freelance-personal-trainer',
  INDEPENDENT_CLASS_PROVIDER: 'independent-provider',
  SPORTS_CLUB: 'sports-club',
};

export const USER_TYPES = {
  BROWSER: 'browser',
  ...PROVIDER_USER_TYPES,
};

export const LISTING_TYPES = {
  CLASS: 'class',
  PERSONAL_TRAINER: 'personal-trainer',
  VENUE_PAGE: 'venue-page',
};

const CLASS_SUB_CATEGORIES = [
  { id: 'hosted-class', label: 'Hosted Class' },
  { id: 'independent-class', label: 'Independent Class' },
];

export const SPORTS_CLUB_SUB_CATEGORIES = [
  { id: 'team-sports', label: 'Team sports' },
  { id: 'individual-sports', label: 'Individual sports' },
  { id: 'outdoor-sports', label: 'Outdoor sports' },
  { id: 'performing-arts', label: 'Dance & performing arts' },
  { id: 'specialist-sports', label: 'Specialist sports' },
  { id: 'combat-sports', label: 'Combat sports' },
];

const PERSONAL_TRAINER_SUB_CATEGORIES = [
  { id: 'employed-personal-trainer', label: 'Gym/Club trainer' },
  { id: 'independent-personal-trainer', label: 'Independent trainer' },
];

export const LISTING_CATEGORIES = {
  GYM: {
    id: 'gym',
    label: 'Gym',
    pluralLabel: 'Gyms',
    description:
      'Discover the perfect place to reach your fitness goals - compare everything from 24-hour, no contract gyms to luxury health clubs and find one that works for you',
    icon: (
      <SvgIcon fontSize="medium">
        <linearGradient id="gymGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset={0} stopColor="var(--gymColourGradientEnd)" />
          <stop offset={1} stopColor="var(--gymColour)" />
        </linearGradient>
        <FitnessCenter sx={{ fill: 'url(#gymGradient)' }} />
      </SvgIcon>
    ),
    iconUnselected: <FitnessCenter fontSize={'medium'} />,
    subCategories: [],
  },
  CLASS: {
    id: 'class',
    label: 'Class',
    pluralLabel: 'Classes',
    description:
      'Experience expert-led workouts within a supportive group environment. Select from classes run by independent providers, gyms, and sports clubs ',
    icon: (
      <SvgIcon fontSize="medium">
        <linearGradient id="classGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset={0} stopColor="var(--classColourGradientEnd)" />
          <stop offset={1} stopColor="var(--classColour)" />
        </linearGradient>
        <Groups sx={{ fill: 'url(#classGradient)' }} />
      </SvgIcon>
    ),
    iconUnselected: <GroupsOutlined fontSize={'medium'} />,
    subCategories: CLASS_SUB_CATEGORIES,
  },
  SPORTS_CLUB: {
    id: 'sports-club',
    label: 'Sports club',
    pluralLabel: 'Sports clubs',
    description:
      'Our broadest sector; we host everything from Rugby clubs to Dance Academies. Find one that interests you with our streamlined search process',

    icon: (
      <SvgIcon fontSize="medium">
        <linearGradient id="sportsClubGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset={0} stopColor="var(--sportsClubColourGradientEnd)" />
          <stop offset={1} stopColor="var(--sportsClubColour)" />
        </linearGradient>
        <SportsHandball sx={{ fill: 'url(#sportsClubGradient)' }} />
      </SvgIcon>
    ),
    iconUnselected: <SportsHandballOutlined fontSize={'medium'} />,
    subCategories: SPORTS_CLUB_SUB_CATEGORIES,
  },
  PERSONAL_TRAINER: {
    id: 'personal-trainer',
    label: 'Personal trainer',
    pluralLabel: 'Personal trainers',
    description:
      "Have professionals tailor fitness plans to your unique goals — whether you're starting out, recovering, or training hard. They offer real results and personal support",
    icon: (
      <SvgIcon fontSize="medium">
        <linearGradient id="personalTrainerGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset={0} stopColor="var(--personalTrainerColour)" />
          <stop offset={1} stopColor="var(--personalTrainerColourGradientEnd)" />
        </linearGradient>
        <Person sx={{ fill: 'url(#personalTrainerGradient)' }} />
      </SvgIcon>
    ),
    iconUnselected: <PersonOutlineOutlined fontSize={'medium'} />,
    subCategories: PERSONAL_TRAINER_SUB_CATEGORIES,
  },
};

export const CARDINALITIES = {
  ONE: 'one',
  MANY: 'many',
  NONE: 'none',
};

// Defines listing configurations for fitness venues.
// Fitness venues can list multiple classes, personal trainers, and venue pages.
// Classes and personal trainers require a venue page to be listed.
// Sports clubs can list multiple classes, personal trainers, and venue pages.
export const LISTING_METADATA = {
  [USER_TYPES.FITNESS_VENUE]: {
    canList: {
      [LISTING_TYPES.CLASS]: {
        cardinality: CARDINALITIES.MANY,
        requires: [LISTING_TYPES.VENUE_PAGE],
        categories: [LISTING_CATEGORIES.CLASS],
      },
      [LISTING_TYPES.PERSONAL_TRAINER]: {
        cardinality: CARDINALITIES.MANY,
        requires: [LISTING_TYPES.VENUE_PAGE],
        categories: [LISTING_CATEGORIES.PERSONAL_TRAINER],
      },
      [LISTING_TYPES.VENUE_PAGE]: {
        cardinality: CARDINALITIES.ONE,
        requires: [],
        categories: [LISTING_CATEGORIES.GYM],
      },
    },
  },
  [USER_TYPES.FREELANCE_PERSONAL_TRAINER]: {
    canList: {
      [LISTING_TYPES.CLASS]: {
        cardinality: CARDINALITIES.MANY,
        requires: [LISTING_TYPES.PERSONAL_TRAINER],
        categories: [LISTING_CATEGORIES.CLASS],
      },
      [LISTING_TYPES.PERSONAL_TRAINER]: {
        cardinality: CARDINALITIES.ONE,
        requires: [],
        categories: [LISTING_CATEGORIES.PERSONAL_TRAINER],
      },
      [LISTING_TYPES.VENUE_PAGE]: {
        cardinality: CARDINALITIES.NONE,
        requires: [],
        categories: [],
      },
    },
  },
  [USER_TYPES.INDEPENDENT_CLASS_PROVIDER]: {
    canList: {
      [LISTING_TYPES.CLASS]: {
        cardinality: CARDINALITIES.MANY,
        requires: [],
        categories: [LISTING_CATEGORIES.CLASS],
      },
      [LISTING_TYPES.PERSONAL_TRAINER]: {
        cardinality: CARDINALITIES.NONE,
        requires: [],
        categories: [LISTING_CATEGORIES.PERSONAL_TRAINER],
      },
      [LISTING_TYPES.VENUE_PAGE]: {
        cardinality: CARDINALITIES.NONE,
        requires: [],
        categories: [],
      },
    },
  },
  [USER_TYPES.SPORTS_CLUB]: {
    canList: {
      [LISTING_TYPES.CLASS]: {
        cardinality: CARDINALITIES.MANY,
        requires: [LISTING_TYPES.VENUE_PAGE],
        categories: [LISTING_CATEGORIES.CLASS],
      },
      [LISTING_TYPES.PERSONAL_TRAINER]: {
        cardinality: CARDINALITIES.MANY,
        requires: [LISTING_TYPES.VENUE_PAGE],
        categories: [LISTING_CATEGORIES.PERSONAL_TRAINER],
      },
      [LISTING_TYPES.VENUE_PAGE]: {
        cardinality: CARDINALITIES.ONE,
        requires: [],
        categories: [LISTING_CATEGORIES.SPORTS_CLUB],
      },
    },
  },
  [USER_TYPES.BROWSER]: {
    canList: {
      [LISTING_TYPES.CLASS]: {
        cardinality: CARDINALITIES.NONE,
        requires: [],
        categories: [],
      },
      [LISTING_TYPES.PERSONAL_TRAINER]: {
        cardinality: CARDINALITIES.NONE,
        requires: [],
        categories: [],
      },
      [LISTING_TYPES.VENUE_PAGE]: {
        cardinality: CARDINALITIES.NONE,
        requires: [],
        categories: [],
      },
    },
  },
};
